<template>
  <v-stepper v-model="stepValue" bg-color="transparent" flat position="relative">
    <v-stepper-header>
      <v-stepper-item color="secondary-darken-1" :title="`${$t('firstStep.title')}`"
        :subtitle="`${$t('firstStep.subTitle')}`" value="1">
      </v-stepper-item>
      <v-divider></v-divider>
      <v-stepper-item color="secondary-darken-1" :title="`${$t('twoStep.title')}`" :subtitle="`${$t('twoStep.subTitle')}`"
        value="2"></v-stepper-item>
    </v-stepper-header>
    <v-stepper-window>
      <v-stepper-window-item value="1">
        <v-card :title="`${$t('firstStep.telTitle')}`" :subtitle="`${$t('firstStep.telSubTitle')}`">
          <v-form @submit.prevent="getOTP" ref="form-otp">
            <v-card-text class="box-otp">
              <div class="tel-no-box">
                <div style="width: 90px;">
                  <SelectPhoneCode v-model:phoneCode="phoneCode" v-model:countryCode="countryCode" variant="outlined"
                    density="comfortable" :disabled="isLoading" required class="custom-height" />
                </div>
                <v-text-field v-model="phoneNumber" variant="outlined" type="text"
                  :placeholder="`${$t('firstStep.telPlaceholder')}`" :rules="[...rules.required, ...rules.phoneNumber]" :disabled="isLoading"
                  required />
              </div>
              <v-btn :disabled="isDisableOTP" :loading="isLoading" type="submit" color="secondary" variant="elevated"
                append-icon="mdi-send-outline">{{ $t('firstStep.telSubmit') }}</v-btn>
            </v-card-text>
          </v-form>
        </v-card>
        <v-card v-if="getDataOTP" :title="`${$t('firstStep.getOTPTitle')}`"
          :subtitle="`${$t('firstStep.getOTPSubTitle')}`">
          <v-otp-input v-model="otp" length="5" :disabled="isLoading"></v-otp-input>
          <div class="otp-detail">
            <div class="count">
              <div class="ref" v-if="getDataOTP">
                {{ $t('firstStep.getOTPRef') }} : {{ getDataOTP.ref }}
                <span :class="timeOTP <= 0 ? 'red' : ''">({{ timeOTP <= 0 ? $t('firstStep.getOTPTimeout') : timeOTP
                }})</span>
              </div>
              <div class="error" v-if="errorOTP">{{ errorOTP }}</div>
            </div>
            <div class="refresh">
              <v-btn :disabled="isDisableAgainOTP" @click="getOTP" color="primary" prepend-icon="mdi-refresh"
                variant="text">{{ $t('firstStep.getOTPAgain') }}</v-btn>
              <div class="time" v-if="timeAgainOTP > 0">
                {{ $t('firstStep.getOTPAgainTime', { timeAgainOTP }) }}
              </div>
            </div>
          </div>
        </v-card>
      </v-stepper-window-item>
      <v-stepper-window-item value="2">
        <v-form @submit.prevent="submit" ref="form-submit">  
          <v-card :class="`card-alert ${isCustomer ? 'success' : 'warning'}`">
            <template v-if="isCustomer"><v-icon>mdi-check-circle</v-icon>{{ $t('twoStep.warningCustomer') }}</template>
            <template v-else><v-icon>mdi-alert-circle</v-icon>{{ $t('twoStep.warningNoCustomer') }}</template>
          </v-card>
          <v-card :title="`${$t('twoStep.businessTypeTitle')}`">
            <v-card-text class="business-type-box">
              <v-btn color="primary" :variant="businessType == 'personal' ? 'elevated' : 'tonal'"
                @click="businessType = 'personal'">{{ $t('twoStep.businessTypePersonal') }}</v-btn>
              <v-btn color="primary" :variant="businessType == 'company' ? 'elevated' : 'tonal'"
                @click="businessType = 'company'">{{ $t('twoStep.businessTypeCompany') }}</v-btn>
            </v-card-text>
          </v-card>
          <v-card :title="`${$t('twoStep.customerTitle')}`">
            <v-card-text class="customer-data-box">
              <template v-if="businessType == 'personal'">
                <v-autocomplete v-model="title" class="prefix" :label="`${$t('twoStep.customerPrefix')}`"
                  :items="[$t('twoStep.prefixMr'), $t('twoStep.prefixMrs'), $t('twoStep.prefixMiss')]" variant="outlined"
                  :no-data-text="`${$t('noData')}`" :rules="rules.required"></v-autocomplete>
                <v-text-field class="first-name" v-model="firstName" variant="outlined"
                  :label="`${$t('twoStep.customerFirstName')}`" :rules="rules.required"></v-text-field>
                <v-text-field v-model="lastName" class="last-name" variant="outlined"
                  :label="`${$t('twoStep.customerLastName')}`" :rules="rules.required"></v-text-field>
                <v-text-field v-model="taxId" class="tax-id" variant="outlined" :label="`${$t('twoStep.customerTaxId')}`"
                  counter="13" :rules="rules.taxId"></v-text-field>
              </template>
              <template v-else>
                <v-text-field v-model="companyName" class="company-name" variant="outlined"
                  :label="`${$t('twoStep.customerCompanyName')}`" :rules="rules.required"></v-text-field>
                <v-text-field v-model="customerName" class="full-name" variant="outlined"
                  :label="`${$t('twoStep.customerName')}`" :rules="rules.required"></v-text-field>
                <v-text-field v-model="taxId" class="tax-id" variant="outlined" :label="`${$t('twoStep.customerTaxId')}`"
                  counter="13" :rules="rules.taxId2"></v-text-field>
              </template>
              <v-text-field class="tel" v-model="tel" variant="outlined" :label="`${$t('twoStep.customerTel')}`"
                counter="10" :rules="rules.phoneNumber" disabled></v-text-field>
              <v-text-field class="email" v-model="email" variant="outlined" :label="`${$t('twoStep.customerEmail')}`"
                :rules="rules.email"></v-text-field>
              <div class="email"></div>
            </v-card-text>
          </v-card>
          <v-card :title="`${$t('twoStep.areaTitle')}`">
            <v-card-text class="area-data-box">
              <v-menu v-model="isStartDate" :close-on-content-click="false" class="start-date">
                <template v-slot:activator="{ props }">
                  <v-text-field :disabled="isLoading" :loading="isLoading" color="primary" class="start-date" v-bind="props"
                    :model-value="datelabel(startDate)" variant="outlined" hide-details readonly
                    :label="`${$t('twoStep.areaStartDate')}`" prepend-inner-icon="mdi-calendar-month"></v-text-field>
                </template>
                <v-date-picker v-model="startDate" color="primary"
                  @update:modelValue="isStartDate = false; searchAvailableAreas();" hide-header></v-date-picker>
              </v-menu>
              <v-menu v-model="isEndDate" :close-on-content-click="false" class="end-date">
                <template v-slot:activator="{ props }">
                  <v-text-field :disabled="isLoading" :loading="isLoading" color="primary" class="end-date" v-bind="props"
                    :model-value="datelabel(endDate)" variant="outlined" hide-details readonly
                    :label="`${$t('twoStep.areaEndDate')}`" prepend-inner-icon="mdi-calendar-month"></v-text-field>
                </template>
                <v-date-picker v-model="endDate" color="primary"
                  @update:modelValue="isEndDate = false; searchAvailableAreas();" hide-header></v-date-picker>
              </v-menu>
              <v-autocomplete color="primary" class="stall-type" :label="`${$t('twoStep.areaStallType')}`"
                v-model="stallType"
                :items="[{ value: 'regularly', text: $t('twoStep.areaStallTypeRegularly') }, { value: 'temporary', text: $t('twoStep.areaStallTypeTemporary') }]"
                item-value="value" item-title="text" variant="outlined" :no-data-text="`${$t('noData')}`"></v-autocomplete>
              <v-autocomplete color="primary" class="market-list" :label="`${$t('twoStep.areaMarket')}`" v-model="market"
                :items="markets" item-value="objectId" item-title="name" variant="outlined"
                :no-data-text="`${$t('noData')}`" auto-select-first @update:modelValue="searchAvailableAreas"
                :disabled="isLoading" :loading="isLoading" :rules="rules.required"></v-autocomplete>
              <v-autocomplete v-if="itemProducts && itemProducts.length" color="primary" class="product-list"
                :label="`${$t('twoStep.areaProduct')}`" v-model="products" :items="itemProducts" item-value="objectId"
                item-title="name" variant="outlined" :no-data-text="`${$t('noData')}`" prepend-inner-icon="mdi-basket" chips
                multiple clearable hide-details>
                <template v-slot:chip="{ item, index, props }">
                  <v-chip v-bind="props" color="primary" :key="`product-list-chip-${index}`">{{ item.title }}</v-chip>
                </template>
              </v-autocomplete>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-title>
              <div>
                {{ $t('twoStep.areaNumber') }}
                <div class="number-area">{{ numOfAreas }} {{ $t('twoStep.areaStall') }}</div>
              </div>
              <v-checkbox v-if="isShowSelectArea" v-model="noAreas" :label="`${$t('twoStep.areaNoStall')}`" color="primary"
                hide-details></v-checkbox>
            </v-card-title>
            <v-card-subtitle v-if="noAreas">
              <div class="title">{{ $t('twoStep.areaInterestSelect') }}</div>
              <v-select v-model="numOfAreas" :items="[1, 2, 3, 4, 5, 6, 7]" variant="outlined" hide-details></v-select>
            </v-card-subtitle>
            <v-card-subtitle v-else>
              <div class="title">{{ $t('twoStep.areaInterestAdd') }}</div>
              <v-btn variant="tonal" prepend-icon="mdi-plus" color="primary" @click="numOfAreas++">{{
                $t('twoStep.areaAddBtn') }}</v-btn>
            </v-card-subtitle>
            <v-card-text class="area-data-box" v-if="!noAreas">
              <template v-for="(area, idxNo) in numOfAreas" :key="idxNo">
                <v-autocomplete v-if="itemAreas && itemAreas.length" class="area-list"
                  :label="`${$t('twoStep.areaSelect')}`" v-model="areas[idxNo]" color="primary" :items="itemAreas"
                  item-value="objectId" item-title="statusBooking.text" variant="outlined" :no-data-text="`${$t('noData')}`"
                  chips :disabled="isLoading" direction="horizontal" :loading="isLoading" :rules="rules.required">
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :subtitle="item.raw.statusBooking.subTitle"
                      :title="item.raw.statusBooking.title">
                      <template v-slot:prepend>
                        <v-avatar :color="item.raw.statusBooking.color">
                          <v-icon color="white">{{ item.raw.statusBooking.icon }}</v-icon>
                        </v-avatar>
                      </template>
                    </v-list-item>
                    <v-divider inset></v-divider>
                  </template>
                  <template v-slot:chip="{ item, index, props }">
                    <v-chip v-bind="props" v-if="item.raw.statusBooking" :key="`area-selection-${idxNo}-${index}`"
                      :color="item.raw.statusBooking.color" :prepend-icon="item.raw.statusBooking.icon">
                      {{ item.raw.statusBooking.text }}
                    </v-chip>
                  </template>
                  <template v-slot:append>
                    <v-icon v-if="idxNo != 0" color="error"
                      @click="deleteItem(areas, idxNo); numOfAreas--">mdi-trash-can-outline</v-icon>
                  </template>
                </v-autocomplete>
              </template>
            </v-card-text>
          </v-card>
  
          <v-card :title="`${$t('twoStep.uploadImageTitle')}`" :subtitle="`${$t('twoStep.uploadImageSubTitle')}`">
            <v-card-text class="image-box">
              <div class="images" v-if="files && files.length">
                <template v-for="(file, indexFile) in files" :key="`files-${indexFile}`">
                  <v-skeleton-loader v-if="file.isImage" :loading="false" height="240" width="240" type="image">
                    <v-img :src="file.url" max-height="240" max-width="240" cover>
                      <template v-slot:error>
                        <v-img src="../assets/myket-logo.png"></v-img>
                      </template>
                    </v-img>
                    <v-btn class="btn-close" color="error" variant="tonal" icon="mdi-close" :disabled="isUploadImage"
                      @click="removeFile(indexFile)" size="x-small">
                    </v-btn>
                  </v-skeleton-loader>
                  <div class="file-box" v-else>
                    <v-icon color="secondary">mdi-file-upload-outline</v-icon>
                    <div class="file-name">{{ file.name }}</div>
                  </div>
                </template>
  
              </div>
              <v-file-input :ref="`file-upload`" v-model="file" :disabled="isUploadImage" :loading="isUploadImage"
                accept="image/*,.pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                @change="uploadImage($event, files)" v-if="!isUploadImage" style="display: none;">
              </v-file-input>
              <v-btn @click="uploadClick" rounded color="primary" variant="tonal" append-icon="mdi-cloud-upload"
                :disabled="isUploadImage" :loading="isUploadImage">
                Upload
              </v-btn>
              <div class="image-upload-subtitle">Files supported : PDF, DOC, DOCX, PNG, JPEG </div>
            </v-card-text>
          </v-card>
          <v-card :title="`${$t('twoStep.otherTitle')}`">
            <v-card-text class="additional-information-box pt-0 px-5" v-if="project && project.setting && project.setting.bookingCustomFields && project.setting.bookingCustomFields.length">
              <v-row class="mt-1">
                <v-col class="px-2 py-1" v-for="(field, indexField) in project.setting.bookingCustomFields" :key="`field-${indexField}`" cols="12"  sm="12" :md="field.width" :lg="field.width" :xl="field.width">
                  <template v-if="field.type == 'text'">
                    <v-text-field 
                    v-model="bookingCustomFields[indexField]" 
                    :label="getLabelName(field)"
                    variant="outlined" type="text" :rules="field.required ? rules.required : []">
                    </v-text-field>
                  </template>
                  <template v-if="field.type == 'number'">
                    <v-text-field 
                    v-model="bookingCustomFields[indexField]" 
                    :label="getLabelName(field)"
                    variant="outlined" type="number" :rules="field.required ? rules.required : []">
                    </v-text-field>
                  </template>
                  <template v-if="field.type == 'date'">
                    <v-menu v-model="bookingCustomFieldsDateMenus[indexField]" :close-on-content-click="false" class="start-date">
                      <template v-slot:activator="{ props }">
                        <v-text-field :disabled="isLoading" :loading="isLoading" color="primary" class="start-date" v-bind="props"
                          :model-value="datelabel(bookingCustomFields[indexField])" variant="outlined" readonly
                          :label="getLabelName(field)" prepend-inner-icon="mdi-calendar-month" :rules="field.required ? rules.required : []"></v-text-field>
                      </template>
                      <v-date-picker v-model="bookingCustomFields[indexField]" color="primary"
                        @update:modelValue="bookingCustomFieldsDateMenus[indexField] = false;" hide-header></v-date-picker>
                    </v-menu>
                  </template>
                  <template v-if="field.type == 'select'">
                    <v-autocomplete color="primary" class="stall-type" :label="getLabelName(field)" v-model="bookingCustomFields[indexField]"
                    :items="field.options" variant="outlined" :no-data-text="`${$t('noData')}`" :rules="field.required ? rules.required : []"></v-autocomplete>
                  </template>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-text class="note-box">
              <v-text-field class="note" v-model="note" variant="outlined"
                :label="`${$t('twoStep.otherNote')}`"></v-text-field>
            </v-card-text>
          </v-card>
        </v-form>
      </v-stepper-window-item>
    </v-stepper-window>
    <v-stepper-actions>
      <template v-slot:next>
        <v-btn v-if="stepValue < 1" @click="clickNext" color="secondary" variant="elevated"
          append-icon="mdi-chevron-right" :disabled="isDisableNext">{{ $t('actionStep.next') }}</v-btn>
        <v-btn v-else @click="submit" :disabled="false" color="secondary" variant="elevated" append-icon="mdi-send-outline">{{ $t('actionStep.submit') }}</v-btn>
      </template>
      <template v-slot:prev>
        <v-btn v-if="stepValue > 0" @click="clearData" color="primary-darken-1" variant="text"
          prepend-icon="mdi-chevron-left">{{ $t('actionStep.back') }}</v-btn>
        <div v-else></div>
      </template>
    </v-stepper-actions>
  </v-stepper>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { FileUpload } from "../classes";
import SelectPhoneCode from "@/components/SelectPhoneCode.vue";
import parsePhoneNumber from 'libphonenumber-js'
let countTimer
export default {
  emits: ["onSubmit"],
  components: {
    SelectPhoneCode
  },
  data() {
    return {
      rules: {
        phoneNumber: [v => !v || parsePhoneNumber(this.phoneCode + v)?.isValid() || this.$t('rule.tel')],
        taxId: [v => !v || this.validateThaiCitizenID(v) || this.$t('rule.taxId')],
        taxId2: [v => !v || this.validateThaiCitizenID(v, 'company') || this.$t('rule.taxId2')],
        email: [v => !v || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) || this.$t('rule.email')],
        required: [v => !!v || this.$t('rule.required')]
      },
      bookingCustomFields: [],
      bookingCustomFieldsDateMenus: [],
      imageFileLabel: ['png', 'jpg', 'gif', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'svg', 'avif', 'webp'],
      projectId: this.$route.params.projectId,
      marketId: this.$route.query.market,
      isLoading: false,
      stepValue: 0,
      phoneNumber: '',
      phoneCode: '',
      countryCode: '',
      otp: null,
      timeOTP: 0,
      errorOTP: null,
      isStartDate: false,
      isEndDate: false,
      isUploadImage: false,
      timeAgainOTP: 0,
      file: null,
      businessType: "personal",
      title: null,
      firstName: '',
      lastName: '',
      companyName: '',
      customerName: '',
      taxId: '',
      tel: '',
      interTel: '',
      email: '',
      startDate: this.$moment().toDate(),
      endDate: this.$moment().add(1, 'months').toDate(),
      market: null,
      stallType: 'regularly',
      products: [],
      numOfAreas: 1,
      noAreas: true,
      areas: [],
      note: '',
      files: [],
      isCustomer: false,
      customFieldsData: [],
      preparedNote: '',
    };
  },
  computed: {
    ...mapGetters({
      project: "project/getProject",
      getDataOTP: "user/getDataOTP",
      getUser: "user/getUser",
      customerUsers: "customerUser/getCustomerUsers",
      isLoadingCustomerUser: "customerUser/getIsLoading",
      markets: "market/getMarkets",
      isLoadingMarkets: "market/getIsLoading",
      itemProducts: "product/getProducts",
      isLoadingProducts: "product/getIsLoading",
      itemAreas: "area/getAreas",
      isLoadingAreas: "area/getIsLoading",
      areaInterest: "areaInterest/getAreaInterest",
      isLoadingAreaInterest: "areaInterest/getIsLoading",
      getLocale: "appSetting/getLocale",
    }),
    isDisableOTP() {
      let res = false
      if (this.getDataOTP) res = true
      if (this.isLoading) res = true
      return res
    },
    isDisableAgainOTP() {
      let res = false
      if (!this.getDataOTP) res = true
      if (this.isLoading) res = true
      if (this.timeAgainOTP > 0) res = true
      if (!this.phoneNumber || (this.phoneNumber && !parsePhoneNumber(this.phoneCode + this.phoneNumber)?.isValid())) res = true
      return res
    },
    isDisableNext() {
      let res = false
      if (!this.getDataOTP) res = true
      if (this.isLoading) res = true
      if (!this.otp || (this.otp && this.otp.length != 5)) res = true
      if (this.timeOTP <= 0) res = true
      return res
    },
    isDisableSubmit() {
      let res = false
      if (!this.getDataOTP) res = true
      if (this.isLoading) res = true
      if (!this.market) res = true
      if (!this.noAreas && this.numOfAreas != this.areas.length) res = true
      if (this.businessType == 'personal') {
        if (!this.title) res = true
        if (!this.firstName) res = true
        if (!this.lastName) res = true
      } else {
        if (!this.companyName) res = true
        if (!this.customerName) res = true
      }
      if(this.project && this.project.setting && this.project.setting.bookingCustomFields && this.project.setting.bookingCustomFields.length) {
        for(let i = 0; i < this.project.setting.bookingCustomFields.length; i++) {
          const field = this.project.setting.bookingCustomFields[i]
          if(field.required) {
            if(!this.bookingCustomFields[i]) {
              res = true
              break
            }
          }
        }
      }
      return res
    },
    isShowSelectArea() {
      return this.project?.setting?.isHideSelectAreas ? false : true
    }
  },
  created() {
    if (this.getLocale == 'lo-LA') {
      this.phoneCode = '+856'
      this.countryCode = 'LA'
    } else {
      this.phoneCode = '+66'
      this.countryCode = 'TH'
    }
  },
  methods: {
    ...mapActions({
      getUserOTP: "user/getUserOTP",
      loginUserOTP: "user/loginUserOTP",
      getMarketByProject: "market/getMarketByProject",
      getCustomerUsers: "customerUser/getCustomerUserByProject",
      getProducts: "product/getProducts",
      getAvailableAreas: "area/getAvailableAreas",
      createAreaInterest: "areaInterest/createAreaInterest"
    }),
    ...mapMutations({
      // clearProject: "project/clearProject",
      clearUser: "user/clearUser",
      clearCustomerUser: "customerUser/clearCustomerUser",
      clearMarket: "market/clearMarket",
      clearArea: "area/clearArea",
      clearProduct: "product/clearProduct",
    }),
    async getOTP() {
      this.errorOTP = null
      if (!this.$refs['form-otp']) {
        return
      }
      const { valid } = await this.$refs['form-otp'].validate()
      if (valid) {
        this.isLoading = true
        try {
          let interTel = parsePhoneNumber(this.phoneCode + this.phoneNumber)?.number
          let tel = parsePhoneNumber(this.phoneCode + this.phoneNumber)?.format("NATIONAL").replace(/\D/g, "")
          await this.getUserOTP({
            tel,
            interTel,
          })
          this.isLoading = false
          if (this.getDataOTP) {
            this.timeOTP = 5
            this.timeAgainOTP = 60
            this.startCounting()
          }
        } catch (error) {
          this.isLoading = false
          console.log(error)
        }
      }
    },
    async clickNext() {
      this.errorOTP = null
      if (this.otp && this.otp.length == 5 && this.timeOTP > 0) {
        try {
          this.isLoading = true
          await this.loginUserOTP({ username: this.getDataOTP.id, verifyCode: this.otp })
          if (this.getUser) {
            this.tel = this.getUser.tel || ''
            this.interTel = this.getUser.interTel || ''
            await this.getMarketByProject(this.projectId)
            await this.getCustomerUsers(this.projectId)
            await this.getProducts(this.projectId)
            if (this.customerUsers?.[0]) this.setCustomer(this.customerUsers[0])
            else if (this.project?.setting?.isBookingCustomer && (!this.customerUsers || this.customerUsers.length == 0)) this.$router.replace(`/notFound`);
            if (this.marketId && this.markets?.length) this.setMarket()
            this.stepValue++
          }
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.timeOTP--
          console.log(error)
          if (error?.response?.data?.error) this.errorOTP = error.response.data.error
          else if (error?.message) this.errorOTP = `${error.message} ${this.otp}`
          else this.errorOTP = error
          this.otp = null
        }
      }
    },
    setMarket() {
      if (this.marketId && this.markets?.length) {
        let market = this.markets.find(m => m.objectId == this.marketId)
        if (market) {
          this.market = market.objectId
          this.searchAvailableAreas(true)
        }
      } else return
    },
    setCustomer(customerUser) {
      let customer
      if (customerUser?.customer) {
        customer = customerUser.customer
        this.isCustomer = true
      } else return
      const { type, title, firstName, lastName, citizenId, companyName, tels, emergencyContactName, emails } = customer
      this.businessType = type || 'personal'
      this.companyName = companyName || ''
      this.customerName = emergencyContactName || ''
      this.title = title || ''
      this.firstName = firstName || ''
      this.lastName = lastName || ''
      this.taxId = citizenId || ''
      this.tel = this.getUser.tel || ''
      this.email = emails?.[0] || ''
    },
    async searchAvailableAreas(isSetArea = false) {
      try {
        this.isLoading = true
        this.areas = []
        await this.getAvailableAreas({
          marketId: this.market,
          startDate: this.startDate,
          endDate: this.endDate,
          type: 'monthly',
        })
        if (isSetArea) {
          for (const area of this.itemAreas) {
            if (area.statusBooking.isAvailable) {
              this.areas = [area.objectId]
              break;
            }
          }

        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        console.log(error)
      }
    },
    async submit() {
      if (!this.$refs['form-submit']) {
        return
      }
      const { valid } = await this.$refs['form-submit'].validate()
      if (!valid) {
        return
      }

      if(this.project && this.project.setting && this.project.setting.bookingCustomFields && this.project.setting.bookingCustomFields.length) {
        this.customFieldsData = []
        for(let i = 0; i < this.project.setting.bookingCustomFields.length; i++) {
          const field = this.project.setting.bookingCustomFields[i]
          let valueField = this.bookingCustomFields[i]
          if(field.type == 'number') {
            valueField = parseInt(valueField)
          }
          this.customFieldsData.push({
            ...field,
            value: valueField
          })
        }
      }
      this.prepareCustomFieldsData(this.customFieldsData)

      let formData = {
        marketId: this.market,
        areasQty: this.numOfAreas,
        areaIds: this.noAreas ? [] : this.areas,

        customerType: this.businessType,
        customerTitle: this.title,
        customerFirstName: this.firstName,
        customerLastName: this.lastName,
        customerCompanyName: this.companyName,
        customerCompanyContact: this.customerName,
        customerCitizenId: this.taxId,
        customerTel: this.tel,
        customerInterTel: this.interTel,
        customerEmail: this.email,
        contractStartDate: this.$moment(this.startDate).startOf('day').toDate(),
        contractEndDate: this.$moment(this.endDate).endOf('day').toDate(),
        stallType: this.stallType,
        productIds: this.products,
        note: this.preparedNote,
        imageFiles: this.files,
        customFields: this.customFieldsData
      }
      let customer
      if (this.customerUsers?.[0] && this.customerUsers[0]?.customer) {
        customer = this.customerUsers[0].customer
      }
      if (this.isCustomer && customer) {
        const { type, title, firstName, lastName, citizenId, companyName, tels, emergencyContactName } = customer
        formData.isNewCustomer = false
        formData.customerId = customer.objectId
        if (title == this.title) formData.customerTitle = undefined
        if (firstName == this.firstName) formData.customerFirstName = undefined
        if (lastName == this.lastName) formData.customerLastName = undefined
        if (companyName == this.companyName) formData.customerCompanyName = undefined
        if (emergencyContactName == this.customerName) formData.customerCompanyContact = undefined
        if (citizenId == this.taxId) formData.customerCitizenId = undefined
        // if (tel == this.tel) formData.customerTel = undefined
      } else {
        formData.isNewCustomer = true
      }
      try {
        this.isLoading = true
        await this.createAreaInterest(formData)
        this.isLoading = false
        this.$emit("onSubmit", this.areaInterest)
      } catch (error) {
        this.isLoading = false
        this.$emit("onSubmit", null, error)
      }
      this.clearData()
    },
    clearData() {
      this.stepValue = 0
      this.phoneNumber = ''
      this.otp = null
      this.timeOTP = 0
      this.errorOTP = null
      this.isStartDate = false
      this.isEndDate = false
      this.isUploadImage = false
      this.timeAgainOTP = 0
      this.file = null
      this.businessType = "personal"
      this.title = null
      this.firstName = ''
      this.lastName = ''
      this.companyName = ''
      this.customerName = ''
      this.taxId = ''
      this.tel = ''
      this.email = ''
      this.startDate = this.$moment().toDate()
      this.endDate = this.$moment().add(1, 'months').toDate()
      this.market = null
      this.stallType = 'regularly'
      this.products = []
      this.numOfAreas = 1
      this.areas = []
      this.note = ''
      this.files = []
      this.isCustomer = false
      this.bookingCustomFields = []
      this.bookingCustomFieldsDateMenus = []
      this.customFieldsData = {}
      // this.clearProject()
      this.clearUser()
      this.clearProduct()
      this.clearMarket()
      this.clearArea()
      this.clearCustomerUser()
      this.$refs['form-otp'].resetValidation()
    },
    validateThaiCitizenID(id, mode) {
      if (id.length != 13 || id.charAt(0).match(/[09]/)) return false;
      if (mode == 'company') {
        if (!(/^\d+$/.test(id))) return false
        return true
      }
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(id.charAt(i)) * (13 - i);
      }
      if ((11 - sum % 11) % 10 != parseInt(id.charAt(12))) {
        return false;
      }
      return true;
    },
    datelabel(date) {
      if (date)
        return `${this.$moment(date).format("D MMMM YYYY")}`;
      else
        return ``;
    },
    uploadClick() {
      if (this.$refs['file-upload'])
        this.$refs['file-upload'].click()
    },
    async uploadImage(e, to = []) {
      try {
        if (this.file) {
          this.isUploadImage = true;
          const splitFileData = this.file.name ? this.file.name.split(".") : null;
          let fileLastName = splitFileData ? splitFileData[splitFileData.length - 1] || splitFileData[0] : null
          let file = await FileUpload.upload(this.file);
          file.name = this.file.name
          file.fileLastName = fileLastName
          file.isImage = this.imageFileLabel.includes(fileLastName) ? true : false

          this.file = null
          this.isUploadImage = false;
          to.push(file);
        }
      } catch (e) {
        this.isUploadImage = false;
        console.log(e);
      }
    },
    removeFile(index) {
      if (index < 0) return;
      else {
        if (this.files && this.files.length) {
          this.files.splice(index, 1);
        }
      }
    },
    startCounting() {
      // this.timeAgainOTP = 10
      countTimer = setInterval(() => {
        this.timeAgainOTP -= 1
        if (this.timeAgainOTP <= 0) {
          this.clearTimer()
        }
      }, 1000)
    },
    clearTimer() {
      clearInterval(countTimer);
    },
    deleteItem(items, index) {
      items.splice(index, 1);
    },
    getLabelName(field) {
      if (this.getLocale == 'lo-LA') {
        return field.label_la
      } else if (this.getLocale == 'en-US') {
        return field.label_en
      } else {
        return field.label_th
      }
    },
    prepareCustomFieldsData(customFields) {
      if(customFields && customFields.length) {
        let customFieldsText = []
        
        customFields.forEach(field => {
          if(field.value) {
            let label = this.getLabelName(field)
            let value = field.value
            if(field.type == 'date') {
              value = this.$moment(value).format("DD/MM/YYYY")
            }
            customFieldsText.push(`${label}: ${value}`)
          }
        })
        
        if(customFieldsText.length > 0) {
          const customFieldsString = customFieldsText.join('\n')
          
          if(this.note) {
            this.preparedNote = `${customFieldsString}\n${this.$t('twoStep.otherNote')}: ${this.note}`
          } else {
            this.preparedNote = customFieldsString
          }
        } else {
          if(this.note) {
            this.preparedNote = `${this.$t('twoStep.otherNote')}: ${this.note}`
          } else {
            this.preparedNote = ''
          }
        }
      } else {
        if(this.note) {
          this.preparedNote = `${this.$t('twoStep.otherNote')}: ${this.note}`
        } else {
          this.preparedNote = ''
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$stepper-item-transition-duration : 5000ms;

.v-stepper {
  min-width: 375px;
  width: 100%;
  max-width: 700px;
  // color: #4CAF50;
  position: relative;
  align-content: center;
  height: 100%;

  @media (max-width: 750px) {
    max-width: 100%;
  }
}

.v-stepper-header {
  position: relative;
  min-width: 300px;
  max-width: 550px;
  width: 90%;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: none;
  .v-stepper-item{
    color: rgb(var(--v-theme-primary));
  }
}

.v-stepper-window {
  position: relative;
  margin: 0 20px;
}

.v-stepper-window-item {
  // position: relative;
  transition-duration: 500ms;
}

.v-card.card-alert {
  text-align: left;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  box-shadow: 8px 0px 0px 0px rgb(var(--v-theme-primary)) inset;

  .v-icon {
    font-size: 30px;
    margin-right: 10px;
  }
}

.v-card.card-alert.warning {
  background: rgba(var(--v-theme-warning-bg), 1);
  color: rgb(var(--v-theme-warning));
  box-shadow: 8px 0px 0px 0px rgb(var(--v-theme-warning)) inset;
}

.v-card.card-alert.success {
  background: rgba(var(--v-theme-success-bg), 1);
  color: rgb(var(--v-theme-success));
  box-shadow: 8px 0px 0px 0px rgb(var(--v-theme-success)) inset;
}

.v-card {
  // box-shadow: var(--v-box-shadow);
  padding: 15px 20px;
  margin: 20px 5px 5px;

  *:deep() {
    .v-card-title {
      font-size: 20px;
      text-align: left;
      color: rgb(var(--v-theme-primary));
    }

    .v-card-subtitle {
      text-align: left;
      color: rgb(var(--v-theme-primary-darken-1));
      opacity: 1;
      text-wrap: pretty;
    }
  }

  .v-card-title {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 20px;
    // text-align: left;
    color: rgb(var(--v-theme-primary-darken-1));

    .number-area {
      font-size: 12px;
      text-align: left;
      color: rgb(var(--v-theme-primary-darken-1));
    }
  }

  .v-card-subtitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: rgb(var(--v-theme-primary-darken-1));
    opacity: 1;
    margin-bottom: 15px;
    text-wrap: pretty;

    .v-input :deep() {

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: 40px;
        padding-top: 0;
        padding-bottom: 0;
      }

      .v-select__selection {
        width: 100%;
        justify-content: center;
      }
    }

    .v-select {
      max-width: 200px;
    }

    .title {
      text-wrap: wrap;
      max-width: 60%;
    }
  }

  .v-divider {
    padding: 0 16px;
    // $divider-border-style: dashed;
    border-style: dashed;
    border-color: rgb(var(--v-border-color));
    margin: 0 16px;
    margin-bottom: 16px;
    opacity: 1;
  }

  .box-otp {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;

    .v-btn {
      height: 38px;
      min-width: 130px;
      max-width: 100%;
    }
  }

  .tel-no-box {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: normal;
    gap: 10px;
    min-width: 300px;
    width: 100%;
    flex: 1;

    .v-input :deep() {
      min-width: 100px;
      input {
        min-height: 40px;
        height: 40px;
        min-width: 100px;
      }
    }
  }

  .custom-height :deep() {
    height: 40px !important;
    .v-field__field,
    .v-field__input {
      min-height: 40px;
      height: 40px;
      padding-top: 5px;
    }
  }

  .otp-detail {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0 16px;

    .count {
      text-align: left;
      color: rgb(var(--v-theme-primary-darken-1));

      .ref {
        span {
          margin-left: 5px;
        }

        span.red {
          color: rgb(var(--v-theme-error));
        }
      }

      .error {
        color: rgb(var(--v-theme-error));
      }
    }

    .refresh {
      text-align: right;
      flex: 1;

      // color: rgb(var(--v-theme-primary));
      .time {
        padding-right: 16px;
        color: rgb(var(--v-theme-primary-darken-1));
        font-size: 12px;
      }
    }
  }

  .business-type-box {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    padding-bottom: 20px;
    gap: 15px;


    .v-btn {
      max-width: 250px;
      height: 49px;
      padding: 0px 95px;
    }
  }

  .customer-data-box {
    display: flex;
    flex-wrap: wrap;
    padding: 0 16px;
    gap: 5px 15px;

    .v-input :deep() {
      position: relative;

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: 40px;
      }

      .v-field {
        padding-right: 2px;
      }

      .v-field__input {
        padding: 0;
        padding-left: 16px;
      }

      .v-autocomplete__selection {
        width: 100%;
        justify-content: center;
      }


      .v-messages {
        // position: absolute;
        // right: 0;
        text-align: left;
      }
    }

    .prefix {
      width: calc(25% - 30px);
    }

    .first-name,
    .last-name {
      width: calc(calc(75% / 2) - 30px);
    }

    .tax-id,
    .tel,
    .email,
    .company-name,
    .full-name {
      width: calc(50% - 8px);

    }

    @media (max-width: 700px) {
      .prefix {
        width: calc(100% - 30px);
      }

      .first-name,
      .last-name {
        width: calc(100% - 30px);
      }

      .tax-id,
      .tel,
      .company-name,
      .full-name {
        width: calc(100% - 30px);
      }
    }
  }

  .area-data-box {
    display: flex;
    flex-wrap: wrap;
    padding: 0 16px;
    gap: 5px 15px;
    position: relative;


    .v-input :deep() {
      position: relative;

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: 40px;
      }

      .v-field {
        padding-right: 2px;
      }

      .v-field__input {
        padding: 0;
        padding-left: 16px;
      }

      .v-messages {
        // position: absolute;
        // right: 0;
        text-align: left;
      }

    }

    .v-autocomplete--multiple :deep() {

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: fit-content;
      }

      .v-field__input {
        gap: 5px;
        flex-wrap: wrap;
        padding-top: 5px;
        padding-bottom: 5px;
      }

      input {
        min-height: 24px;
      }

    }

    .v-autocomplete--single :deep() {
      .list-item-box {
        padding: 5px 10px;
        cursor: pointer;
      }
    }

    .list-item-box {
      padding: 5px 10px;
      cursor: pointer;
    }

    .start-date,
    .end-date {
      width: calc(calc(70% / 2) - 0px);
    }

    .stall-type {
      width: calc(30% - 30px);
    }


    .market-list,
    .product-list {
      width: calc(calc(100% / 2) - 30px);
    }

    .area-list {
      width: 100%;
    }

    @media (max-width: 700px) {

      .start-date,
      .end-date {
        width: calc(100% - 0px);
        margin-bottom: 20px;
      }

      .stall-type {
        width: calc(100% - 30px);
      }

      .market-list,
      .product-list {
        width: calc(100% - 30px);
      }
    }

  }

  .additional-information-box {
    .v-input :deep() {
      position: relative;

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: 40px;
      }

      .v-field {
        padding-right: 2px;
      }

      .v-field__input {
        padding: 0;
        padding-left: 16px;
      }

      .v-messages {
        text-align: left;
      }

    }

    .v-autocomplete--multiple :deep() {

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: fit-content;
      }

      .v-field__input {
        gap: 5px;
        flex-wrap: wrap;
        padding-top: 5px;
        padding-bottom: 5px;
      }

      input {
        min-height: 24px;
      }

    }

    .v-autocomplete--single :deep() {
      .list-item-box {
        padding: 5px 10px;
        cursor: pointer;
      }
    }

    .list-item-box {
      padding: 5px 10px;
      cursor: pointer;
    }
  }
  

  .image-box {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: center;
    align-items: center;
    min-height: 270px;
    border: 1px dashed rgb(var(--v-border-color));
    border-radius: 15px;
    margin: 0 16px;

    .images {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 5px;
      width: 100%;
      height: 100%;
      margin: 15px 0;

      .v-skeleton-loader,
      .file-box {
        position: relative;
        width: calc(calc(100% / 3) - 5px);
        aspect-ratio: 1;
        // min-height: 200px;
        background: rgb(var(--v-theme-on-surface-variant));
        border-radius: 15px;

        .btn-close {
          position: absolute;
          right: 5px;
          top: 5px;
        }
      }

      .file-box {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        padding: 15px;

        .v-icon {
          font-size: 50px;
        }

        .file-name {
          max-height: 50%;
          word-wrap: unset;
          word-break: break-all;
          word-spacing: 0;
          width: 100%;
        }
      }
    }

    .btn-upload {
      margin-top: 15px;
      cursor: pointer;

      &.loading {
        cursor: not-allowed;
      }

      .v-chip {
        font-size: 16px;
        padding: 0px 25px;
        --v-chip-height: 36px;
        border-radius: 10px;

        .v-progress-circular {
          margin: 5px;
        }
      }
    }

    .image-upload-subtitle {
      text-align: center;
      margin-top: 15px;
      width: 100%;
      color: rgb(var(--v-theme-primary-darken-1));
    }
  }

  .note-box {
    display: flex;
    flex-wrap: wrap;
    padding: 0 16px;
    gap: 5px 15px;
    position: relative;


    .v-input :deep() {
      position: relative;

      input,
      .v-field,
      .v-field__field,
      .v-field__input {
        min-height: 40px;
        height: 40px;
      }

      .v-field {
        padding-right: 2px;
      }

      .v-field__input {
        padding: 0;
        padding-left: 16px;
      }

      .v-messages {
        text-align: left;
      }
    }
  }

}

.card-form {
  border: 1px solid var(--v-border-color);
  border-radius: 10px;
  padding: 15px 30px;
  background: #F7F9FC;
  max-width: 600px;

  @media (max-width: 414px) {
    padding: 8px 15px;
  }

  .card-form-body {
    text-align: center;

    @media (max-width: 414px) {
      font-size: 15px;
    }

    .card-form-row {
      text-align: left;
    }

    // .card-form-row {
    //   margin-bottom: 20px;

    .column-2,
    .column-3 {
      // padding: 0 0 15px 15px;
      display: flex;
      width: 100%;
      flex-wrap: wrap;
    }

    .column-2>* {
      width: calc(100% / 2);

      // min-width: 300px;
      @media (max-width: 414px) {
        width: 100%;
      }
    }

    .column-2>*:first-child {
      padding-right: 20px;

      @media (max-width: 414px) {
        padding-right: 0px;
      }
    }

    .column-3>* {
      text-align: center;
      width: calc(100% / 3);
    }

    label {
      margin-top: 10px;
      margin-bottom: 0;
    }

    input[type=text],
    input[type=tel],
    input[type=email],
    select {
      width: 100%;
      padding: 8px 15px;
      margin: 5px 0;
      margin-top: 2px;
      display: inline-block;
      border: 1px solid #CDD6E1;
      border-radius: 4px;
      box-sizing: border-box;
    }

    input[type=text]::placeholder,
    input[type=tel]::placeholder,
    input[type=email]::placeholder,
    select:invalid {
      color: #C0CBD8;
    }

    select {
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-image:
        linear-gradient(45deg, transparent 50%, gray 50%),
        linear-gradient(135deg, gray 50%, transparent 50%), ;
      // linear-gradient(to right, #CDD6E1, #CDD6E1);
      background-position:
        calc(100% - 20px) calc(1em + 2px),
        calc(100% - 15px) calc(1em + 2px), ;
      // calc(100% - 2.5em) 0.5em;
      background-size:
        5px 5px,
        5px 5px, ;
      // 1px 1.5em;
      background-repeat: no-repeat;
    }

    input[type=radio] {
      // border: 1px solid #CDD6E1;
      margin: 5px;
    }

    input[type=radio]:checked {
      // outline: 1px solid #CDD6E1;
    }

    input[type=submit] {
      // width: 100%;
      // max-width: 400px;
      font-size: 18px;
      background-color: #46bd82;
      color: white;
      padding: 7px 12px;
      // margin: 8px 0;
      margin-top: 15px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }

    input[type=submit]:hover {
      background-color: #3ba46f;
    }

    // }
  }
}

.v-stepper-actions {
  margin-top: 15px;
}
</style>
